// useMarketingSEO — sets document <title> + upserts canonical meta tags for
// the current marketing page. Restores the static defaults from
// /public/index.html on unmount so non-marketing routes don't inherit
// stale per-program copy.
//
// CMS MIGRATION: today the `seo` block is read from
// /content/marketing/programs/<slug>.json (and /content/marketing/about|affiliates.json).
// When the marketing CMS API ships per-page seo, the merge in
// `mergeMarketingProgram()` already promotes a backend `seo` field if
// present. The only thing to delete then is the local JSON files.
import { useEffect } from "react";

const TAGS = {
  description: { selector: 'meta[name="description"]', attr: "name", value: "description" },
  ogTitle: { selector: 'meta[property="og:title"]', attr: "property", value: "og:title" },
  ogDescription: { selector: 'meta[property="og:description"]', attr: "property", value: "og:description" },
  ogImage: { selector: 'meta[property="og:image"]', attr: "property", value: "og:image" },
  ogUrl: { selector: 'meta[property="og:url"]', attr: "property", value: "og:url" },
  twitterCard: { selector: 'meta[name="twitter:card"]', attr: "name", value: "twitter:card" },
  twitterImage: { selector: 'meta[name="twitter:image"]', attr: "name", value: "twitter:image" },
};

function upsert(key, content) {
  if (typeof document === "undefined") return null;
  const spec = TAGS[key];
  if (!spec) return null;
  let el = document.head.querySelector(spec.selector);
  let created = false;
  const previous = el ? el.getAttribute("content") : null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(spec.attr, spec.value);
    document.head.appendChild(el);
    created = true;
  }
  el.setAttribute("content", content);
  return { el, created, previous };
}

export function useMarketingSEO({ title, description, ogImage, canonicalUrl } = {}) {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const prevTitle = document.title;
    const restorers = [];

    if (title) {
      document.title = title;
    }
    if (description) {
      const r = upsert("description", description);
      if (r) restorers.push({ ...r, key: "description" });
      const og = upsert("ogDescription", description);
      if (og) restorers.push({ ...og, key: "ogDescription" });
    }
    if (title) {
      const og = upsert("ogTitle", title);
      if (og) restorers.push({ ...og, key: "ogTitle" });
    }
    if (ogImage) {
      const og = upsert("ogImage", ogImage);
      if (og) restorers.push({ ...og, key: "ogImage" });
      const tw = upsert("twitterImage", ogImage);
      if (tw) restorers.push({ ...tw, key: "twitterImage" });
      const card = upsert("twitterCard", "summary_large_image");
      if (card) restorers.push({ ...card, key: "twitterCard" });
    }
    if (canonicalUrl) {
      const og = upsert("ogUrl", canonicalUrl);
      if (og) restorers.push({ ...og, key: "ogUrl" });
    }

    return () => {
      document.title = prevTitle;
      for (const r of restorers) {
        if (r.created) {
          r.el.parentNode && r.el.parentNode.removeChild(r.el);
        } else if (r.previous != null) {
          r.el.setAttribute("content", r.previous);
        }
      }
    };
  }, [title, description, ogImage, canonicalUrl]);
}
